/* =========================================================================
   Resume an in-progress game — ROUND-granular snapshots only (never a
   mid-question state). gameController writes a snapshot at every between-rounds
   moment (onRoundComplete), and on "Save & leave" mid-round for the round in
   progress (which then restarts from Q1); it clears it when the game finishes,
   when a fresh game starts, and on explicit sign-out. The snapshot is identity-scoped
   (userId when authed, guest:<guestId> otherwise) so a login/logout switch can
   never resume someone else's run — a mismatched snapshot is discarded on read.

   Pure logic (shape/version validation + identity scoping) is unit-tested over
   the injectable KVStore seam (house pattern, dailyBlessing.ts); the live
   helpers below adapt to localStorage / api / DOM.
   ========================================================================= */
import type { KVStore } from './dailyBlessing';
import type { LogEntry } from './engine';
import { fmtCents } from './engine';
import { ROUNDS } from './data/packs';
import { getCurrentUser, isAuthenticated } from './api';
import { getGuestId } from './guestId';

export const SNAPSHOT_KEY = 'catholics.gameInProgress';

export interface GameSnapshot {
  /** 1 = original shape; 2 adds `hard` (the round's 🔥 flag). Both are read. */
  v: 1 | 2;
  /** userId (authed) or 'guest:<guestId>' — never resume across identities. */
  identity: string;
  /** The round to begin on resume (1-based round = nextRoundIndex + 1). */
  nextRoundIndex: number;
  bankCents: number;
  log: LogEntry[];
  dykFired: number;
  theoPopsThisGame: number;
  registerPromptShown: boolean;
  hardServed: number[];
  /** v2: was the round being saved played in 🔥 hard mode? A device-only resume
      restores it (the server save point always carried its own hardMode). */
  hard?: boolean;
  savedAtIso: string;
}

/* ------------------------------- pure logic ------------------------------- */

/** The identity string a snapshot is scoped to. PURE. */
export function identityFor(userId: string | null, guestId: string): string {
  return userId ? userId : 'guest:' + guestId;
}

/** Validate a parsed snapshot candidate: version, shape, and a sane
    nextRoundIndex (0..totalRounds-1). Snapshots used to exist only BETWEEN
    rounds (1..), but "Save & leave" (design round 2026-08-21 §A) writes one for
    the round IN PROGRESS, so 0 is now a deliberate value: "restart Round 1 with
    your log/bank" — the controller clears instead when there is nothing to
    keep, so a 0 here always carries something. Returns the typed snapshot or
    null. PURE. */
export function validateSnapshot(parsed: unknown, totalRounds: number): GameSnapshot | null {
  if (!parsed || typeof parsed !== 'object') return null;
  const s = parsed as Record<string, unknown>;
  if (s.v !== 1 && s.v !== 2) return null;
  if (s.v === 2 && typeof s.hard !== 'boolean') return null;
  if (typeof s.identity !== 'string' || s.identity.length === 0) return null;
  const idx = s.nextRoundIndex;
  if (typeof idx !== 'number' || !Number.isInteger(idx) || idx < 0 || idx >= totalRounds) return null;
  if (typeof s.bankCents !== 'number' || !Number.isFinite(s.bankCents)) return null;
  if (!Array.isArray(s.log)) return null;
  if (typeof s.dykFired !== 'number' || typeof s.theoPopsThisGame !== 'number') return null;
  if (typeof s.registerPromptShown !== 'boolean') return null;
  if (!Array.isArray(s.hardServed) || !(s.hardServed as unknown[]).every((x) => typeof x === 'number')) return null;
  if (typeof s.savedAtIso !== 'string') return null;
  return s as unknown as GameSnapshot;
}

/* ------------------------------- storage seam ------------------------------- */

function deviceStore(): KVStore | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function writeSnapshot(snap: GameSnapshot, store: KVStore | null = deviceStore()): void {
  try {
    store?.setItem(SNAPSHOT_KEY, JSON.stringify(snap));
  } catch {
    /* private mode — the run simply won't survive a reload */
  }
}

export function clearSnapshot(store: KVStore | null = deviceStore()): void {
  try {
    // KVStore has no removeItem; an empty value reads back as invalid JSON → null.
    store?.setItem(SNAPSHOT_KEY, '');
  } catch {
    /* ignore */
  }
}

/** Read the stored snapshot for `identity`. Invalid or identity-mismatched
    snapshots are DISCARDED (cleared) — you never resume someone else's run. */
export function readSnapshot(
  identity: string,
  store: KVStore | null = deviceStore(),
  totalRounds: number = ROUNDS.length,
): GameSnapshot | null {
  let snap: GameSnapshot | null = null;
  try {
    const raw = store?.getItem(SNAPSHOT_KEY);
    if (!raw) return null;
    snap = validateSnapshot(JSON.parse(raw) as unknown, totalRounds);
  } catch {
    snap = null;
  }
  if (!snap || snap.identity !== identity) {
    clearSnapshot(store); // corrupt or someone else's — drop it
    return null;
  }
  return snap;
}

/* ------------------------------- live helpers ------------------------------- */

/** The current player identity (userId when authed, guest:<guestId> otherwise). */
export function currentIdentity(): string {
  const u = isAuthenticated() ? getCurrentUser() : null;
  return identityFor(u ? u.userId : null, getGuestId());
}

/** NOTE (Phase 1, PLAN-progression §2): for a SIGNED-IN player the authoritative
    save point is now the server's (POST /api/game/session → `resume`), which
    gameController consults on start; this device snapshot is still written at
    every round boundary and remains the guest path plus the offline fallback and
    the source of the prior rounds' log. The CTA below labels from the local
    snapshot only — a fresh install with a server-side run in progress therefore
    reads "Start Round" but still resumes when tapped. */

/** Refresh the home Start CTA: with a valid snapshot the primary button becomes
    "▶ Resume — Round N of R · $X banked" and the quiet "Start a new game" link
    appears; otherwise the standard Start Round button. Called on home render. */
export function renderResumeCta(): void {
  const btn = document.getElementById('startBtn');
  const alt = document.getElementById('startFreshLink');
  if (!btn) return;
  const snap = readSnapshot(currentIdentity());
  if (snap) {
    btn.textContent = `▶ Resume — Round ${snap.nextRoundIndex + 1} of ${ROUNDS.length} · ${fmtCents(snap.bankCents)} banked`;
    alt?.classList.remove('hidden');
  } else {
    btn.textContent = 'Start Round';
    alt?.classList.add('hidden');
  }
}
